/**
 * Silly Farm Predictive Search Enhancement
 * - Shows popular searches when input is focused but empty
 * - Stores and displays recent searches in localStorage
 * - Enhances the native Shopify predictive search
 */

class SFPredictiveSearchManager {
  constructor() {
    this.STORAGE_KEY = 'sf_recent_searches';
    this.MAX_RECENT = 5;
    this.searchContainers = document.querySelectorAll('[data-sf-predictive-search]');

    if (this.searchContainers.length === 0) return;

    this.init();
  }

  init() {
    this.searchContainers.forEach(container => {
      this.setupContainer(container);
    });

    // Listen for form submissions to save recent searches
    document.addEventListener('submit', this.handleFormSubmit.bind(this));

    // Listen for popular term clicks
    document.addEventListener('click', this.handlePopularClick.bind(this));
  }

  setupContainer(container) {
    const input = container.querySelector('input[type="search"]');
    const resultsContainer = container.querySelector('[data-predictive-search]');
    const popularSection = container.querySelector('[data-popular-searches]');
    const recentSection = container.querySelector('[data-recent-searches]');
    const clearBtn = container.querySelector('[data-clear-recent]');

    if (!input || !resultsContainer) return;

    // Show popular/recent on focus when empty
    input.addEventListener('focus', () => {
      if (input.value.trim() === '') {
        this.showIdleState(container, resultsContainer, popularSection, recentSection);
      }
    });

    // Hide on blur (with delay for click handling)
    input.addEventListener('blur', () => {
      setTimeout(() => {
        if (!container.contains(document.activeElement)) {
          this.hideIdleState(container, resultsContainer);
        }
      }, 200);
    });

    // Hide idle state when typing
    input.addEventListener('input', () => {
      if (input.value.trim() !== '') {
        this.hideIdleState(container, resultsContainer);
      } else {
        this.showIdleState(container, resultsContainer, popularSection, recentSection);
      }
    });

    // Clear recent searches
    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.clearRecentSearches();
        this.updateRecentSearchesUI(recentSection);
      });
    }

    // Update recent searches UI
    if (recentSection) {
      this.updateRecentSearchesUI(recentSection);
    }
  }

  showIdleState(container, resultsContainer, popularSection, recentSection) {
    container.classList.add('sf-predictive-search--focused');

    // Show the results container
    resultsContainer.style.display = 'block';

    // Show popular searches
    if (popularSection) {
      popularSection.style.display = 'block';
    }

    // Update and show recent searches if any
    if (recentSection) {
      this.updateRecentSearchesUI(recentSection);
    }
  }

  hideIdleState(container, resultsContainer) {
    container.classList.remove('sf-predictive-search--focused');

    // Only hide if the predictive search element is not open
    const predictiveSearch = container.querySelector('predictive-search');
    if (predictiveSearch && !predictiveSearch.hasAttribute('open') && !predictiveSearch.hasAttribute('loading')) {
      resultsContainer.style.display = 'none';
    }
  }

  handleFormSubmit(e) {
    const form = e.target;
    if (form.getAttribute('role') !== 'search') return;

    const input = form.querySelector('input[type="search"]');
    if (!input) return;

    const term = input.value.trim();
    if (term) {
      this.saveRecentSearch(term);
    }
  }

  handlePopularClick(e) {
    const popularLink = e.target.closest('[data-popular-term]');
    if (!popularLink) return;

    const term = popularLink.dataset.popularTerm;
    if (term) {
      this.saveRecentSearch(term);
    }
  }

  getRecentSearches() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  }

  saveRecentSearch(term) {
    try {
      let recent = this.getRecentSearches();

      // Remove if already exists (to move to front)
      recent = recent.filter(s => s.toLowerCase() !== term.toLowerCase());

      // Add to front
      recent.unshift(term);

      // Limit to max
      recent = recent.slice(0, this.MAX_RECENT);

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(recent));
    } catch (e) {
      // localStorage not available
    }
  }

  clearRecentSearches() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (e) {
      // localStorage not available
    }
  }

  updateRecentSearchesUI(recentSection) {
    if (!recentSection) return;

    const recentList = recentSection.querySelector('[data-recent-list]');
    if (!recentList) return;

    const recent = this.getRecentSearches();

    // Clear existing items safely
    while (recentList.firstChild) {
      recentList.removeChild(recentList.firstChild);
    }

    if (recent.length === 0) {
      recentSection.classList.remove('has-items');
      return;
    }

    recentSection.classList.add('has-items');

    // Build items using safe DOM methods
    recent.forEach(term => {
      const li = document.createElement('li');
      li.className = 'sf-predictive-search__recent-item';

      const link = document.createElement('a');
      link.href = '/search?q=' + encodeURIComponent(term);
      link.className = 'sf-predictive-search__recent-link';

      // Create SVG icon using namespace
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'sf-predictive-search__recent-icon');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', '2');

      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', '12');
      circle.setAttribute('cy', '12');
      circle.setAttribute('r', '10');

      const polyline = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
      polyline.setAttribute('points', '12 6 12 12 16 14');

      svg.appendChild(circle);
      svg.appendChild(polyline);
      link.appendChild(svg);

      // Use textContent for safe text insertion
      const textSpan = document.createElement('span');
      textSpan.textContent = term;
      link.appendChild(textSpan);

      li.appendChild(link);
      recentList.appendChild(li);
    });
  }
}

// Override the predictive search results rendering to use our custom styles
class SFPredictiveSearchRenderer {
  constructor() {
    this.observer = new MutationObserver(this.handleMutations.bind(this));
    this.observeContainers();
  }

  observeContainers() {
    document.querySelectorAll('[data-predictive-search]').forEach(container => {
      this.observer.observe(container, {
        childList: true,
        subtree: true
      });
    });
  }

  handleMutations(mutations) {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          this.enhanceResults(node);
        }
      });
    });
  }

  enhanceResults(element) {
    // Add our custom classes to product items
    const productItems = element.querySelectorAll('.predictive-search__item--link-with-thumbnail');
    productItems.forEach(item => {
      if (!item.classList.contains('sf-enhanced')) {
        item.classList.add('sf-enhanced');
        this.enhanceProductItem(item);
      }
    });

    // Style the headings
    const headings = element.querySelectorAll('.predictive-search__heading');
    headings.forEach(heading => {
      if (!heading.classList.contains('sf-enhanced')) {
        heading.classList.add('sf-enhanced');
        heading.classList.add('sf-predictive-search__section-title');
      }
    });
  }

  enhanceProductItem(item) {
    // The items are already styled by Shopify, we just add enhancements
    const image = item.querySelector('.predictive-search__image');
    if (image) {
      image.classList.add('sf-predictive-search__product-image');
    }

    const content = item.querySelector('.predictive-search__item-content');
    if (content) {
      content.classList.add('sf-predictive-search__product-content');
    }

    const vendor = item.querySelector('.predictive-search__item-vendor');
    if (vendor) {
      vendor.classList.add('sf-predictive-search__product-vendor');
    }

    const title = item.querySelector('.predictive-search__item-heading');
    if (title) {
      title.classList.add('sf-predictive-search__product-title');
    }

    const price = item.querySelector('.price');
    if (price) {
      price.classList.add('sf-predictive-search__product-price');
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SFPredictiveSearchManager();
    new SFPredictiveSearchRenderer();
  });
} else {
  new SFPredictiveSearchManager();
  new SFPredictiveSearchRenderer();
}
